import { InputField, SelectSimple } from '@advanza/input'
import { Button, Divider } from '@advanza/ui'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import BaseModal, {
    BaseModalFooter,
    BaseModalHeader,
    BaseModalMainContent,
} from '@trustoo/components/modal/BaseModal'
import LoadingDots from '@trustoo/components/ui/LoadingDots'
import { useModalToast } from 'components/misc/Toaster'
import useAddFreeLeads from 'hooks/useAddFreeLeads'
import { FREE_LEAD_TYPE_FREE_TRIAL } from 'interfaces/FreeLeads'
import { useState } from 'react'
import { getRejectionMessage } from 'utils/getRejectionMessage'

interface Props {
    providerId: number
}

const FreeTrialModal = NiceModal.create(({ providerId }: Props) => {
    const modal = useModal()
    const [showInstructions, setShowInstructions] = useState(false)
    const initialNewEntity = {
        nr_leads: 3,
        comment: '',
        free_lead_type: FREE_LEAD_TYPE_FREE_TRIAL,
    }
    const { newEntity, onChangeNewEntity, isSaving, onSave, errors } = useAddFreeLeads(
        providerId,
        initialNewEntity,
    )
    const { addModalToast } = useModalToast()

    const closeModal = () => {
        modal.resolve()
        modal.hide()
    }

    const handleSave = () => {
        // `onSave` already put the field messages in `errors`; stay open and name the rest, since
        // nothing was saved and the button is the retry.
        onSave().then(closeModal, (rejection: unknown) =>
            addModalToast({
                id: 'save-error',
                msg: getRejectionMessage(
                    rejection,
                    'Something went wrong while starting the free trial',
                ),
                icon: 'error',
                red: true,
            }),
        )
    }

    return (
        <BaseModal
            closeOnOutSideClick={false}
            preventClose={isSaving}
            mobileFullscreen
            maxWidth={674}>
            <BaseModalHeader>
                <h2 className="text-xl font-bold">Start a free trial</h2>
            </BaseModalHeader>
            <BaseModalMainContent>
                <div className="mb-4">
                    {showInstructions ? (
                        <>
                            <b>How it works:</b>
                            <ul className="list-disc pl-5">
                                <li>
                                    You can activate the free trial by giving the provider between 1
                                    and 10 free trial leads
                                </li>
                                <li>
                                    The provider gets access to all the premium features during the
                                    trial
                                </li>
                                <li>
                                    The provider has the ability to reclaim free trial leads, and
                                    get a free trial lead back if the reclaim is accepted
                                </li>
                                <li>
                                    If all the free trial leads are used, the profile will go on
                                    pause ("limit reached") for 15 days
                                </li>
                                <li>
                                    During this period it still is possible to reclaim free trial
                                    leads. The trial ends automatically after the 15 days.
                                </li>
                                <li>
                                    If the provider doesn't become premium during this 15 days
                                    period, the profile will be snoozed for 6 weeks.
                                </li>
                                <li>There will be a day limit of 1</li>
                                <li>Phone leads and website clicks are off by default</li>
                                <li>There won't be a signing date</li>
                                <li>There won't be an invoice</li>
                            </ul>
                            <Button onClick={() => setShowInstructions(false)} name="link">
                                Hide
                            </Button>
                        </>
                    ) : (
                        <Button onClick={() => setShowInstructions(true)} name="link">
                            How does it work?
                        </Button>
                    )}
                </div>
                <SelectSimple
                    name="nr_leads"
                    value={newEntity.nr_leads}
                    legend
                    placeholder="Number of free trial leads"
                    options={[...Array(10).keys()]
                        .map((id) => id + 1)
                        .map((id) => ({
                            value: id,
                            name: `${id} free trial lead${id === 1 ? '' : 's'}`,
                        }))}
                    onChange={(event) => {
                        if (typeof event.target.value === 'number') {
                            onChangeNewEntity({ nr_leads: event.target.value })
                        }
                    }}
                    error={!!errors?.nr_leads}
                />
                <Divider m />
                <InputField
                    name="comment"
                    value={newEntity.comment}
                    min={0}
                    max={100}
                    error={errors?.comment}
                    legend
                    placeholder="Comment (optional)"
                    onChange={(event) =>
                        onChangeNewEntity({ [event.target.name]: event.target.value })
                    }
                />
            </BaseModalMainContent>
            <BaseModalFooter className="flex flex-wrap justify-between gap-2">
                <Button name="link" onClick={closeModal} disabled={isSaving}>
                    Cancel
                </Button>
                <Button onClick={handleSave} disabled={newEntity.nr_leads === 0 || isSaving}>
                    {isSaving ? <LoadingDots /> : 'Start free trial'}
                </Button>
            </BaseModalFooter>
        </BaseModal>
    )
})

export default FreeTrialModal
